import { useMemo, useState } from 'react';
import { works, worksFilters } from '@/mocks/works';
import WorkCard from '@/components/feature/WorkCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import useSeo from '@/hooks/useSeo';

type SortKey = 'date' | 'rating' | 'name';

const PAGE_SIZE = 12;

export default function Works() {
  const [type, setType] = useState<string>('全部');
  const [year, setYear] = useState<string>('全部');
  const [roleType, setRoleType] = useState<string>('全部');
  const [sort, setSort] = useState<SortKey>('date');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = works.filter((w) => {
      if (type !== '全部' && w.type !== type) return false;
      if (year !== '全部' && String(w.year) !== year) return false;
      if (roleType !== '全部' && w.roleType !== roleType) return false;
      return true;
    });

    const sorted = [...list];
    if (sort === 'date') sorted.sort((a, b) => b.year - a.year);
    if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    if (sort === 'name') sorted.sort((a, b) => a.title.localeCompare(b.title, 'zh-Hans-CN'));
    return sorted;
  }, [type, year, roleType, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeFilterText = [
    type !== '全部' ? type : null,
    year !== '全部' ? `${year} 年` : null,
    roleType !== '全部' ? roleType : null,
  ]
    .filter(Boolean)
    .join(' · ');

  useSeo({
    title: '吉泽明步作品全集 - 影视类型分类及介绍',
    description: `吉泽明步作品全集，按影视类型分类整理，包含电影、电视剧与短片作品介绍。当前筛选：${
      activeFilterText || '全部作品'
    }，共 ${filtered.length} 部。`,
    keywords: '吉泽明步作品,作品全集,影视类型,电影,电视剧,短片,作品分类',
  });

  const reset = () => {
    setType('全部');
    setYear('全部');
    setRoleType('全部');
    setSort('date');
    setPage(1);
  };

  const updateFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '作品' }]} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            作品展示
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            收录吉泽明步在电影、电视剧与短片中的全部作品，可按类型、年份与角色类型筛选，并切换排序方式。
          </p>
        </div>
      </div>

      <div className="container-page py-8 md:py-10">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <FilterGroup
              label="作品类型"
              options={['全部', ...worksFilters.types]}
              value={type}
              onChange={updateFilter(setType)}
            />
            <FilterGroup
              label="上映年份"
              options={['全部', ...worksFilters.years.map((y) => String(y))]}
              value={year}
              onChange={updateFilter(setYear)}
            />
            <FilterGroup
              label="角色类型"
              options={['全部', ...worksFilters.roleTypes]}
              value={roleType}
              onChange={updateFilter(setRoleType)}
            />
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-background-200/70 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              共 <span className="font-medium text-foreground-950">{filtered.length}</span> 部作品
              {activeFilterText && <span className="text-foreground-500"> · {activeFilterText}</span>}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500">排序</span>
              <div className="flex items-center rounded-full border border-background-300/70 bg-background-100 p-1">
                {[
                  { key: 'date' as SortKey, label: '发布时间' },
                  { key: 'rating' as SortKey, label: '热度' },
                  { key: 'name' as SortKey, label: '名称' },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={[
                      'rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer',
                      sort === option.key
                        ? 'bg-primary-600 text-background-50'
                        : 'text-foreground-600 hover:text-primary-700',
                    ].join(' ')}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              没有找到匹配的作品
            </h3>
            <p className="mt-2 text-sm text-foreground-600">
              当前的筛选条件组合下暂无结果，试试调整或重置筛选条件。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

function FilterGroup({ label, options, value, onChange }: FilterGroupProps) {
  return (
    <div>
      <p className="mb-2.5 text-sm font-medium text-foreground-800">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={[
              'rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer',
              value === option
                ? 'bg-primary-600 text-background-50'
                : 'border border-background-300/70 bg-background-100 text-foreground-600 hover:border-primary-300 hover:text-primary-700',
            ].join(' ')}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}